// libraries
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import Input from '/react4xp/components/Form/Input'
import Select from '/react4xp/components/Form/Select'
import Button from '/react4xp/components/Button'
import MasonryGrid from '/react4xp/components/MasonryGrid'
import IdeaCard from '/react4xp/components/Idea/IdeaCard'
import Link from '/react4xp/components/Link'
// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { GeneralSearch } from '/react4xp/components/Svg/Icons/General'
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

interface IdeaOverviewFiltersProps {
    section?: {
        bgColor?: string
        isWhite?: boolean
    }
    title: string
    filters: {
        category: string[]
        region: string[]
    }
    ideasLiked: string[]
    strings: {
        all: string
        category: string
        region: string
        sort: string
        newest: string
        mostLiked: string
        search: string
        searchButton: string
        loadMore: string
        of: string
        ideas: string
        deleteIdeaButton?: string
        noIdeasFound?: string
    },
    endpoint: string,
    services: {
        likeContent: string
    },
    startValues: {
        page?: number
        pageSize?: number
        search?: string
        category?: string
        region?: string
        sort?: string
    }
    isLogged?: boolean
}

export default function IdeaOverviewFilters({
    title,
    filters,
    ideasLiked,
    strings,
    services,
    endpoint,
    startValues,
    isLogged,
    section
}: IdeaOverviewFiltersProps) {
    const [ideas, setIdeas] = useState<any[]>([])
    const [page, setPage] = useState(startValues?.page || 1)
    const [inputValue, setInputValue] = useState(startValues?.search || '')
	const [searchTerm, setSearchTerm] = useState(startValues?.search || '')
    const [selectedCategory, setSelectedCategory] = useState( startValues?.category || '')
    const [selectedRegion, setSelectedRegion] = useState( startValues?.region || '')
    const [sort, setSort] = useState(startValues?.sort)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [loadMore, setLoadMore] = useState(false)
    const [total, setTotal] = useState(0)

    useEffect(() => {
        handleFetchIdeas()
	}, [searchTerm, selectedCategory, selectedRegion, page, sort])

    const handleFetchIdeas = async ()=> {
        if(!endpoint) return;

        // only clear the Ideas array when it is not loading more content
        if (!loadMore) {
            setIdeas([]);
        }

        
        const origin = window.location.origin;
        const url = new URL(`${origin}${endpoint}`);

        const queryParams = new URLSearchParams();

        queryParams.append('type', 'nominations')
        if (page) queryParams.append('page', String(page));
        if (startValues?.pageSize) queryParams.append('pageSize', String(startValues?.pageSize));
        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedCategory) queryParams.append('category', selectedCategory);
        if (selectedRegion) queryParams.append('region', selectedRegion);
        if (sort) queryParams.append('sort', sort);
    
        url.search = queryParams.toString();

        updateCurrentUrl()

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`);
            }
            const data = await response.json();
            
            if(loadMore){
                setIdeas([...ideas, ...data.hits]);
                setLoadMore(false)
            }else{
                setIdeas(data.hits);
            }

            setHasNextPage(data.hasNextPage);
            setTotal(data.total)

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    }

	const handleSearchTerm = () => {
        setPage(1)
        setSearchTerm(inputValue)

        setTimeout(() => {
			const searchResultsElement = document.getElementById('search-results')
			if (searchResultsElement) {
				const headerOffset = 0
				const elementPosition = searchResultsElement.getBoundingClientRect().top + window.scrollY
				const offsetPosition = elementPosition - headerOffset
	
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})
			}
		}, 100)
	}
    
	const handleLoadMoreIdeas = () => {
        if(hasNextPage){
            setLoadMore(true)
            setPage(page + 1)
        }
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin;
        const updatedUrl = new URL(`${origin}${window.location.pathname}`);
        const queryParams = new URLSearchParams();

        if (searchTerm) queryParams.append('search', searchTerm);
        if (selectedCategory) queryParams.append('category', selectedCategory);
        if (selectedRegion) queryParams.append('region', selectedRegion);
        if (sort) queryParams.append('sort', sort);

        updatedUrl.search = queryParams.toString();
        window.history.pushState({}, '', updatedUrl);
    
    }

    useEffect(() => {
        setIdeas([])
    }, [selectedCategory, selectedRegion])

    return (
        <main id="main" className="page-idea page-idea--overview">
            <Section
                bgColor='white'
                className='hero-filters'
                paddingTop='small'
            >

                <div className='container relative z2'>

                    <h2 className='h1'>
                        {title}
                    </h2>

                    <div className='hero-filters__search'>
                                
                        <Input
                            id='search'
                            size='large'
                            name={strings.search}
                            label={strings.search}
                            type='text'
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={() => handleSearchTerm()}
                        />

                        <Button
                            color='black'
                            hover='red'
                            noUnderline
                            smallBorders
                            onClick={() => handleSearchTerm()}
                        >
                            <span>{strings.searchButton}</span>
                            <GeneralSearch variant='outline' />
                        </Button>

                    </div>

                    <div className='hero-filters__filters'>
                        <Select
                            defaultValue={selectedCategory}
                            label={strings.category}
                            id='filter-category'
                            size='large'
                            name='Kategori'
                            onChange={(e) => {
                                setPage(1)
                                e.target.value === 'all' ? setSelectedCategory('') : setSelectedCategory(e.target.value)
                            }}
                        >
                            <option value="all">{strings.all}</option>
                            {filters.category?.map((categoryOption) => {
                                return <option key={categoryOption} value={categoryOption}>{categoryOption}</option>
                            })}
                        </Select>

                        <Select
                            defaultValue={selectedRegion}
                            label={strings.region}
                            id='filter-region'
                            size='large'
                            name='Område'
                            onChange={(e) => {
                                setPage(1)
                                e.target.value === 'all' ? setSelectedRegion('') : setSelectedRegion(e.target.value)
                            }}
                        >
                            <option value="all">{strings.all}</option>
                            {filters.region?.map((regionOption) => {
                                return <option key={regionOption} value={regionOption}>{regionOption}</option>
                            })}
                        </Select>

                        <Select
                            defaultValue={sort}
                            label={strings.sort}
                            id='filter-sort'
                            size='medium'
                            name='Sortér'
                            onChange={(e) => {
                                setPage(1)
                                setSort(e.target.value)
                            }}
                        >
                            <option value='newest'>{strings.newest}</option>
                            <option value='likes'>{strings.mostLiked}</option>
                        </Select>
                    </div>

                </div>

            </Section>

            <Section
                bgColor={section?.bgColor}
                paddingBottom="small"
                className='all-ideas'
                id="search-results"
            >
                <div className='container'>

                    <div className='all-ideas__details'>

                    <div className='total'>
                        <p className={clsx(section?.isWhite && 'white')}>
                            {ideas.length} {strings?.of} {total} {strings?.ideas}
                        </p>
                    </div>

                    </div>

                    {ideas ? (
                        <div className='all-ideas__grid'>

                            <MasonryGrid
                                items={ideas || []}
                                columnGutter={20}
                                maxColumnCount={3}
                                breakpointCols={{
                                    default: 3,
                                    1200: 2,
                                    676: 1
                                }}
                                columnWidth={275}
                                overscanBy={99}
                                render={({ data }) => 
                                <IdeaCard
                                    image={data.image?.link}
                                    alt={data.image?.alt}
                                    status={data.statusData?.status}
                                    statusText={data.statusData?.label}
                                    title={data.title}
                                    extraField={data.extraField}
                                    desc={data.description}
                                    likes={data.likes}
                                    isLiked={ideasLiked.includes(data.id)}
                                    comments={data.comments}
                                    href={data.url?.href}
                                    contentId={data.id}
                                    services={services}
                                    isInteractive
                                    isLogged={isLogged}
                                    strings={strings}
                                />
                            }
                            />

                            {hasNextPage && (
                                <div className="load-more load-more--left-aligned">
                                    <Link
                                        text={strings.loadMore}
                                        onClick={handleLoadMoreIdeas}
                                        iconPosition='right'
                                        icon={<UxArrowRight />}
                                        noMotionMobile
                                        isWhite={section?.isWhite}
                                    />
                                </div>
                            )}

                        </div>
                    ) : (
                        <div className='all-ideas__no-results'>
                            <p className={clsx(
                                section?.isWhite && 'white'
                            )}>
                                Ingen idéer funnet
                            </p>
                        </div>
                    )}
                    
                </div>
            </Section>
        </main>
    )
}